import { NavLink, useLocation } from 'react-router-dom';

import SvgIcon from '../SvgIcon/SvgIcon';

import { SidebarModuleDTO } from '../../types/sidebar';

interface SidebarModuleLinkV2Props {
  module: SidebarModuleDTO;

  compactMode?: boolean;
  sidebarExpanded?: boolean;
}

/**
 * 🧭 SidebarModuleLinkV2
 *
 * Link directo de módulo.
 *
 * Compatible:
 * ✅ ERP
 * ✅ POS compacto
 */
const SidebarModuleLinkV2: React.FC<SidebarModuleLinkV2Props> = ({
  module,
  sidebarExpanded = true,
}) => {
  // =====================================================
  // 📍 Ruta actual
  // =====================================================
  const { pathname } = useLocation();

  // =====================================================
  // 🔒 Estado
  // =====================================================
  const isDisabled = module.disabled;

  // =====================================================
  // 📍 Activo
  // =====================================================
  const isActive =
    module.route &&
    (pathname === module.route || pathname.startsWith(module.route + '/'));

  // =====================================================
  // 🎨 Compact mode
  // =====================================================
  const isCompactCollapsed = !sidebarExpanded;

  // =====================================================
  // 🎨 Base styles
  // =====================================================
  const baseClass = `
    group
    relative
    flex
    items-center
    rounded-sm
    font-medium
    duration-300
    ease-in-out

    ${isCompactCollapsed ? 'justify-center px-2 py-3' : 'gap-2.5 px-4 py-2'}
  `;

  // =====================================================
  // 🎨 Active styles
  // =====================================================
  const activeStyles = `
    text-white
    bg-graydark

    before:absolute
    before:left-0
    before:top-0
    before:h-full
    before:w-1
    before:rounded-r-md
    before:bg-primary
    before:shadow-[0_0_6px_rgba(59,130,246,0.8)]
  `;

  // =====================================================
  // 🎨 Content
  // =====================================================
  const content = (
    <div
      title={isCompactCollapsed ? module.name : undefined}
      className={`
        ${baseClass}

        ${
          isDisabled
            ? `
              text-gray-500
              cursor-not-allowed
              opacity-60
            `
            : isActive
            ? activeStyles
            : `
              text-bodydark1
              hover:bg-graydark
              dark:hover:bg-meta-4
            `
        }
      `}
    >
      {/* 🔹 Icon */}
      <SvgIcon name={module.icon} className="shrink-0 text-white" />

      {/* 🔹 Label */}
      {!isCompactCollapsed && <span>{module.name}</span>}
    </div>
  );

  // =====================================================
  // 🔒 Disabled
  // =====================================================
  if (isDisabled || !module.route) {
    return <div className="mb-2">{content}</div>;
  }

  // =====================================================
  // 🔗 Link
  // =====================================================
  return (
    <NavLink to={module.route} className="mb-2 block">
      {content}
    </NavLink>
  );
};

export default SidebarModuleLinkV2;
